import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from './Button';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  headerClassName?: string;
  usePortal?: boolean;
  titleCentered?: boolean;
  backdropBlur?: boolean;
  backdropClassName?: string;
  animationStyle?: 'default' | 'slide-up';
  style?: React.CSSProperties;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  className,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  headerClassName,
  usePortal = true,
  titleCentered = false,
  backdropBlur = false,
  backdropClassName,
  animationStyle = 'default',
  style,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const scrollLockActiveRef = useRef(false);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(isOpen);

  // Mobile-safe scroll lock management - prevents viewport expansion
  useEffect(() => {
    if (isOpen && !scrollLockActiveRef.current) {
      // Calculate scrollbar width before locking to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Lock scroll and compensate for scrollbar width
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      scrollLockActiveRef.current = true;
    } else if (!isOpen && scrollLockActiveRef.current) {
      // Unlock scroll and remove padding compensation
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      scrollLockActiveRef.current = false;
    }

    // Always clean up on unmount
    return () => {
      if (scrollLockActiveRef.current) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        scrollLockActiveRef.current = false;
      }
    };
  }, [isOpen]);

  // Handle mounting when the modal opens
  useEffect(() => {
    if (!isOpen) return;

    setShouldRender(true);
    previousFocusRef.current = document.activeElement as HTMLElement;
  }, [isOpen]);

  // Kick off the entrance animation only after the modal content is actually mounted
  useEffect(() => {
    if (!shouldRender || !isOpen) return;

    let animationFrame: number | null = null;
    let focusTimer: number | null = null;

    animationFrame = window.requestAnimationFrame(() => {
      setIsAnimating(true);

      // Slight delay so focus happens once the panel is visible
      focusTimer = window.setTimeout(() => {
        modalRef.current?.focus();
      }, 80);
    });

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
      if (focusTimer !== null) {
        window.clearTimeout(focusTimer);
      }
    };
  }, [shouldRender, isOpen]);

  // Handle exit animation timing before we unmount the modal
  useEffect(() => {
    if (isOpen || !shouldRender) return;

    setIsAnimating(false);

    const exitTimer = window.setTimeout(() => {
      setShouldRender(false);

      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    }, animationStyle === 'slide-up' ? 300 : 200); // Match animation duration

    return () => {
      window.clearTimeout(exitTimer);
    };
  }, [isOpen, shouldRender, animationStyle]);

  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Handle focus trap
  useEffect(() => {
    if (!isOpen) return;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          event.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  if (!shouldRender) return null;

  // Create portal container element (only used if usePortal is true)
  const getPortalContainer = () => {
    let container = document.getElementById('modal-root');
    if (!container) {
      container = document.createElement('div');
      container.id = 'modal-root';
      document.body.appendChild(container);
    }
    return container;
  };

  // Modal JSX element
  const modalElement = (
    <div
      className="fixed inset-0 z-[2147483002] flex items-center justify-center max-w-[100vw] overflow-x-hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-description' : undefined}
    >
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 transition-opacity duration-200 ease-linear',
          backdropBlur && 'backdrop-blur-sm',
          isAnimating ? 'opacity-100' : 'opacity-0',
          backdropClassName
        )}
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div
        ref={modalRef}
        className={cn(
          'relative z-[2147483002] w-full',
          // Mobile-optimized sizing
          'max-w-lg sm:max-w-lg md:max-w-xl lg:max-w-2xl',
          // Mobile viewport sizing - full height on small screens
          'max-h-[95vh] sm:max-h-[90vh]',
          // Enable vertical scrolling when content exceeds max-height
          'overflow-y-auto',
          // Mobile-specific positioning
          'mx-2 sm:mx-4',
          'bg-[#FDF6EC] dark:bg-background border border-[rgba(60,50,30,0.10)] dark:border-border rounded-3xl shadow-lg',
          // Mobile-optimized scrolling
          'overscroll-contain',
          // Performance optimizations - GPU acceleration, prevent flickering
          '[backface-visibility:hidden] [perspective:1000px]',
          // Smooth animations with custom easing
          'transition-[opacity,transform]',
          animationStyle === 'slide-up' ? 'duration-300' : 'duration-200',
          isAnimating
            ? 'opacity-100 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]'
            : 'opacity-0 [transition-timing-function:cubic-bezier(0.4,0,1,1)]',
          className
        )}
        style={{
          willChange: 'opacity, transform',
          // Hide pre-animation state to prevent flicker on desktop
          visibility: shouldRender && !isAnimating ? 'hidden' : undefined,
          // Use translate3d for GPU acceleration, subtle scale for smooth feel
          transform: isAnimating
            ? 'translate3d(0, 0, 0) scale(1)'
            : animationStyle === 'slide-up'
              ? 'translate3d(0, 2rem, 0) scale(0.97)'
              : 'translate3d(0, 0.5rem, 0) scale(0.96)',
          ...style,
        }}
        tabIndex={-1}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div
            className={cn(
              titleCentered ? 'relative' : 'flex items-center justify-between',
              headerClassName ?? 'px-[18px] py-[14px]'
            )}
          >
            <div className={cn(
              titleCentered ? 'text-center' : 'flex-1 pr-2'
            )}>
              {title && (
                <h2
                  id="modal-title"
                  className="font-serif text-[clamp(1.4rem,5vw,1.7rem)] font-normal text-foreground leading-tight tracking-tight"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id="modal-description"
                  className="text-sm text-muted-foreground mt-1 leading-relaxed"
                >
                  {description}
                </p>
              )}
            </div>

            {showCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className={cn(
                  "flex-shrink-0",
                  // Mobile-optimized touch target
                  "h-10 w-10 sm:h-8 sm:w-8",
                  "mobile-touch-target",
                  titleCentered ? "absolute right-2 top-2 sm:right-4 sm:top-4" : "ml-2"
                )}
                aria-label="Close modal"
              >
                <X className="h-5 w-5 sm:h-4 sm:w-4" />
              </Button>
            )}
          </div>
        )}

        {/* Content */}
        <div className={cn(
          // Mobile-optimized padding
          'p-4 sm:p-6',
          (title || showCloseButton) && 'pt-0'
        )}>
          {children}
        </div>
      </div>
    </div>
  );

  // Render with or without portal based on usePortal prop
  return usePortal
    ? ReactDOM.createPortal(modalElement, getPortalContainer())
    : modalElement;
};

export default Modal;
